// react
import React, { useMemo } from 'react';
import styled from 'styled-components';

// component
import { Title } from '@/components/atoms/Title/Title';
import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';
import { WebinarCard } from '@/components/atoms/WebinarCard/WebinarCard';

import { CardContainer } from '@/components/templates/CardContainer/CardContainer';

// hooks/
import { useWindowSize } from '@/hooks/';

// store
import { Webinar as WebinarType } from '@karehero/models';

// theme
import theme from '@/theme';

interface RenderWebinarsProps {
  webinars?: WebinarType[];
  isLoading: boolean;
  onClick?: (webinar: Partial<WebinarType>) => void;
}

/**
 * RenderWebinars is a helper component that renders the the webinars.
 */
const RenderWebinars = ({
  webinars,
  isLoading,
  onClick,
}: RenderWebinarsProps) => {
  // hooks/
  const [width] = useWindowSize();

  // memos
  const maxColumns = useMemo(() => {
    if (width < theme.breakpoint.mdValue) return 1;
    if (width < theme.breakpoint.lgValue) return 2;
    if (width < theme.breakpoint.xlValue) return 3;
    return 4;
  }, [width]);

  if (!webinars || webinars.length === 0) return null;
  return (
    <>
      <CardContainer maxColumns={maxColumns} isPreventHeightMatch>
        {webinars.map((webinar) => (
          <WebinarCard
            key={webinar.title}
            webinar={webinar}
            isLoading={isLoading}
            onClick={onClick}
          />
        ))}
      </CardContainer>
    </>
  );
};

/**
 * WebinarOverview is a page that displays all of the webinars that
 * are available to the user.
 */
export const WebinarOverview = ({
  webinars,
  isLoading,
  onClick,
}: WebinarOverviewProps) => {
  // memos
  const dummyWebinars = useMemo(() => {
    const webinars: WebinarType[] = [];

    for (let i = 0; i < 10; i++) {
      webinars.push({
        _id: `webinar_${i}`,
        title: `Webinar ${i}`,
        description: `Description for Webinar ${i}`,
        date: new Date().toISOString(),
        startTime: '12:00',
        endTime: '13:00',
        speaker: 'Bob Ross',
        tags: [
          {
            _key: 'tag1',
            _type: 'tag',
            label: 'Leadership',
            value: 'leadership1',
          },
        ],
        headerImage: {
          _key: `headerImage_key_${i}`,
          _type: 'image',
          asset: {
            _ref: `headerImage_ref_${i}`,
            _type: 'image',
          },
        },
        callToActionImage: {
          _key: `headerImage_key_${i}`,
          _type: 'image',
          asset: {
            _ref: `headerImage_ref_${i}`,
            _type: 'image',
          },
        },
        content: [
          {
            _key: `content_key_${i}`,
            _type: 'block',
            children: [
              {
                _key: `child_key_${i}`,
                _type: 'span',
                marks: ['strong'],
                text: `Webinar Content ${i}`,
              },
            ],
            markDefs: [],
            style: 'normal',
          },
        ],
      });
    }

    return webinars;
  }, []);

  return (
    <StyledWebinar>
      <PageHeader>
        <Title isNoMargin>Webinars</Title>
        <Description>
          This is where you’ll find the webinars that are available to you.
        </Description>
      </PageHeader>
      <LoadingTransition
        isLoading={isLoading}
        loading={
          <Content>
            <RenderWebinars webinars={dummyWebinars} isLoading />
          </Content>
        }
        content={
          <Content>
            <RenderWebinars
              webinars={webinars}
              isLoading={false}
              onClick={onClick}
            />
          </Content>
        }
      />
    </StyledWebinar>
  );
};

const Description = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[80]};
`;

const PageHeader = styled.div``;

const Content = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[32]};
  flex-wrap: wrap;
`;

const StyledWebinar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[36]};
`;

interface WebinarOverviewProps {
  webinars?: WebinarType[];
  isLoading: boolean;
  onClick?: (webinar: Partial<WebinarType>) => void;
}

WebinarOverview.displayName = 'WebinarOverview';

export default WebinarOverview;
